/**
 * Shared contract + helpers for the generic bulk search results surface.
 *
 * A BulkSearchDescriptor adapts one entity (payment / artifact / execution) to
 * the shared BulkSearchResults component: it supplies the table columns, the
 * server-side PropertyFilter properties, how to build + dispatch the search for
 * a page of results, and how to read the result back out of the store. All the
 * UI/pagination/filter plumbing lives once in BulkSearchResults.
 */
import StatusIndicator, { StatusIndicatorProps } from '@cloudscape-design/components/status-indicator';
import { TableProps } from '@cloudscape-design/components/table';
import { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import { toFlowState } from 'src/store/slices/paymentDetail/paymentDetailFormat';
import { RegionFailure, RegionPage } from 'src/store/slices/regionFanOut';
import { AppDispatch, RootState } from 'src/store/store';

/** Args for one page of a bulk search: the queried ids + pagination + filter tokens. */
export interface BulkSearchArgs {
  ids: string[];
  pageNumber: number;
  pageSize: number;
  /** PropertyFilter tokens grouped by property key (equals operator only). */
  filters: Record<string, string[]>;
}

/** BulkSearchArgs for a single region's independent table query. */
export interface BulkSearchRegionArgs extends BulkSearchArgs {
  region: string;
}

/** Server result projection the component renders from. */
export interface BulkSearchResultState<TRow> {
  /** All regions' rows in one list; the only list in a single-region search. */
  items: TRow[];
  total: number;
  loading: boolean;
  error: string | null;
  /** Each region's own page, kept separate because regions paginate apart. */
  regionResults: RegionPage<TRow>[];
  searchedRegions: string[];
  regionErrors: RegionFailure[];
  /** Regions whose per-region (independent table) query is currently in flight. */
  loadingRegions: string[];
}

/** Per-entity adapter consumed by BulkSearchResults. */
export interface BulkSearchDescriptor<TRow> {
  /** Lowercase entity noun, e.g. "payment". */
  entity: string;
  /** Page/results heading, e.g. "Bulk payment search". */
  title: string;
  /** Route path of this results page, e.g. "/payments" (breadcrumb). */
  path: string;
  /** Placeholder for the id entry box, e.g. "Enter multiple artifact IDs, one per line". */
  bulkPlaceholder: string;
  columns: TableProps.ColumnDefinition<TRow>[];
  /** Column ids shown by default; the rest stay available to toggle in Preferences. Defaults to all. */
  defaultVisibleColumns?: string[];
  /** Extra columns derived from the current rows — one per ref type (header = type, cell = id). */
  dynamicColumns?: (items: TRow[]) => TableProps.ColumnDefinition<TRow>[];
  /** Server-side filterable properties (status/state/type). */
  filteringProperties: PropertyFilterProps.FilteringProperty[];
  /** Dispatch the fan-out search across every target region (one shared page/filter). */
  run: (dispatch: AppDispatch, args: BulkSearchArgs) => void;
  /** Dispatch one region's independent-table query (merges into that region only). */
  runRegion: (dispatch: AppDispatch, args: BulkSearchRegionArgs) => void;
  /** Clear server state (called on unmount). */
  clear: (dispatch: AppDispatch) => void;
  /** Read the result projection out of the store. */
  select: (state: RootState) => BulkSearchResultState<TRow>;
}

/** The search API caps a single request at 100 identifiers. */
export const MAX_IDS = 100;

/** Parse a textarea / query-param blob into a trimmed, non-empty, capped id list. */
export const parseIds = (raw: string): string[] =>
  raw.split(/[\n,]/).map((s) => s.trim()).filter(Boolean).slice(0, MAX_IDS);

/** Reduces PropertyFilter tokens to `{ propertyKey: [values] }` (equals, non-empty). */
export const queryToFilterMap = (query: PropertyFilterProps.Query): Record<string, string[]> => {
  const out: Record<string, string[]> = {};
  for (const token of query.tokens) {
    if (typeof token.propertyKey !== 'string' || typeof token.value !== 'string') continue;
    const value = token.value.trim();
    if (token.operator !== '=' || !value) continue;
    (out[token.propertyKey] = out[token.propertyKey] ?? []).push(value);
  }
  return out;
};

/** First value of a filter key, or undefined — for single-valued API fields. */
export const firstOf = (filters: Record<string, string[]>, key: string): string | undefined =>
  filters[key]?.[0];

/** Display a string, or "-" when empty / the literal "null". */
export const dash = (value?: string | null): string => (value && value !== 'null' ? value : '-');

/** Beacon state → StatusIndicator type (SUCCESS/FAILED/in-progress). */
export const flowStatusType = (state?: string): StatusIndicatorProps.Type => {
  const flow = toFlowState(state);
  return flow === 'SUCCESS' ? 'success' : flow === 'FAILED' ? 'error' : 'in-progress';
};

/** Status cell: colored indicator (by state) showing the status label. */
export const StatusCell = ({ state, label }: { state?: string; label?: string }) => (
  <StatusIndicator type={flowStatusType(state)}>{dash(label)}</StatusIndicator>
);

/** Comma-join a list for a cell, or "-" when empty. */
export const joinList = (values?: (string | number)[] | null): string =>
  values && values.length ? values.map(String).join(', ') : '-';

/** One column per ref type across the rows: header = type, cell = that row's id(s) of that type. */
export function refTypeColumns<TRow>(
  items: TRow[],
  getRefs: (row: TRow) => { type?: string; id: string }[],
): TableProps.ColumnDefinition<TRow>[] {
  const types = new Set<string>();
  for (const row of items) for (const ref of getRefs(row)) if (ref.type) types.add(ref.type);
  return Array.from(types).sort().map((type) => ({
    id: `type:${type}`,
    header: type,
    cell: (row: TRow) => joinList(getRefs(row).filter((r) => r.type === type).map((r) => r.id)),
  }));
}
